import React, { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import GridLayout, { Responsive, WidthProvider, Layout } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import { trpc } from '../../lib/trpc'
import PanelRenderer from './PanelRenderer'
import { cn, debounce } from '../../lib/utils'
import { shouldProcessHotkey } from '../../lib/keyboard-utils'
import { useLayoutPersistence } from '../../hooks/use-layout-persistence'
import { useUIStore } from '../../stores/ui-store'
import { LayoutGrid, MessageCircle, StickyNote, Calendar, Bot } from 'lucide-react'

const ResponsiveGridLayout = WidthProvider(Responsive)
const FixedGridLayout = WidthProvider(GridLayout)

interface PanelWorkspaceProps {
  workspaceId: string
  className?: string
}

interface Panel {
  id: string
  type: string
  title: string
  position_x: number
  position_y: number
  width: number
  height: number
  is_minimized: boolean
  is_locked: boolean
  data?: any
}

export default function PanelWorkspace({ workspaceId, className }: PanelWorkspaceProps) {
  const { focusMode, focusedPanelId, setFocusedPanelId } = useUIStore()

  const { data: allPanels = [], refetch } = trpc.panel.list.useQuery(
    { workspaceId },
    { 
      staleTime: 5 * 60 * 1000, // 5 minutes - prevent constant refetching
      gcTime: 60000,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      enabled: !!workspaceId,
    }
  )

  // Track hidden panels
  const [hiddenPanelIds, setHiddenPanelIds] = useState<Set<string>>(new Set())

  // Note: We no longer need a separate panels array since we handle visibility in the render method

  const [containerWidth, setContainerWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth // Use full width
    }
    return 1600
  })
  const isMobile = containerWidth < 768
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false)
  const touchStartXRef = useRef<number | null>(null)
  const touchStartYRef = useRef<number | null>(null)
  const SWIPE_THRESHOLD = 40 // px
  
  useEffect(() => {
    const updateWidth = () => {
      const container = document.querySelector('.panel-workspace-container')
      if (container) {
        const width = container.clientWidth || window.innerWidth
        setContainerWidth(width)
      }
    }
    
    updateWidth()
    window.addEventListener('resize', updateWidth)
    return () => window.removeEventListener('resize', updateWidth)
  }, [])

  // Detect keyboard (visualViewport on modern mobile browsers)
  useEffect(() => {
    const vv = (window as any).visualViewport
    if (!vv) return
    const handler = () => {
      const isOpen = vv.height && window.innerHeight && vv.height < window.innerHeight - 80
      setIsKeyboardOpen(!!isOpen)
    }
    vv.addEventListener('resize', handler)
    handler()
    return () => vv.removeEventListener('resize', handler)
  }, [])

  const {
    onLayoutChange: onLayoutPersistenceChange,
    performAutoSave,
    isAutoSaving
  } = useLayoutPersistence({
    workspaceId,
    panels: allPanels,
    autoSaveInterval: 30000, // Auto-save every 30 seconds
    enabled: true // Re-enable with proper synchronization
  })
  
  const fullscreenPanel = allPanels.find(panel => {
    try {
      const panelData = typeof panel.data === 'string' ? JSON.parse(panel.data) : panel.data
      return panelData?.isFullscreen === true
    } catch {
      return false
    }
  })

  // Server-side focus update for chip interactions
  const setFocusMutation = trpc.panel.setFocus.useMutation({
    onSuccess: () => {
      refetch()
    }
  })

  // Top chips ribbon removed per updated design (bottom dock is the single control)

  // Bottom dock for mobile panel access
  const MobilePanelDock: React.FC = () => {
    const dockPanels = allPanels // include minimized too so they are reachable
    if (dockPanels.length <= 1) return null

    const scrollToActive = useCallback(() => {
      if (!focusedPanelId) return
      const el = document.querySelector(
        `[data-panel-pill-id="${focusedPanelId}"]`
      ) as HTMLElement | null
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' })
      }
    }, [focusedPanelId])

    useEffect(() => {
      scrollToActive()
    }, [scrollToActive])

    return (
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-20 pb-[env(safe-area-inset-bottom)]">
        <div className="mx-auto max-w-screen-sm">
          <div className="m-2 rounded-2xl border border-gray-200 bg-white/95 backdrop-blur shadow-lg">
            <div className="flex items-center gap-1.5 px-2 py-2 overflow-x-auto no-scrollbar">
              {dockPanels.map(panel => {
                const isActive = focusedPanelId === panel.id
                return (
                  <button
                    key={panel.id}
                    data-panel-pill-id={panel.id}
                    onClick={() => {
                      setFocusedPanelId(panel.id)
                      setFocusMutation.mutate({ id: panel.id, focused: true })
                      if (panel.is_minimized) {
                        toggleMinimizeMutation.mutate({ id: panel.id })
                      } else {
                        refetch()
                      }
                    }}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full border text-xs transition-all',
                      isActive
                        ? 'h-8 px-3 bg-krushr-primary/10 text-krushr-primary border-krushr-primary/30'
                        : 'h-7 px-2 bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                    )}
                    aria-pressed={isActive}
                    aria-label={`Focus ${panel.title || panel.type}`}
                    title={panel.title}
                  >
                    <span className={cn('w-2 h-2 rounded-full', isActive ? 'bg-krushr-primary' : 'bg-gray-300')} />
                    <span className="truncate max-w-[38vw]">{panel.title || panel.type}</span>
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    )
  }
  const updatePositions = trpc.panel.updatePositions.useMutation({
    onSuccess: () => {
      console.log('✅ Panel positions saved successfully')
      // Don't refetch on success - the UI state is already correct
      // This prevents panels from snapping back after successful moves
    },
    onError: (error) => {
      console.error('❌ Failed to save panel positions:', error)
      // On error, refetch to revert to saved positions
      refetch()
    }
  })

  const toggleFullscreen = trpc.panel.toggleFullscreen.useMutation({
    onSuccess: () => {
      refetch()
    }
  })

  const toggleMinimize = trpc.panel.toggleMinimize.useMutation({
    onSuccess: () => {
      refetch()
    }
  })
  // Separate instance for dock interaction to avoid stale closures in renderer
  const toggleMinimizeMutation = trpc.panel.toggleMinimize.useMutation({
    onSuccess: () => refetch(),
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't process hotkeys if user is typing in an input field
      if (!shouldProcessHotkey(e)) {
        return
      }

      if (e.key === 'Escape' && fullscreenPanel) {
        e.preventDefault()
        toggleFullscreen.mutate({ id: fullscreenPanel.id })
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'm' && focusedPanelId) {
        e.preventDefault()
        toggleMinimize.mutate({ id: focusedPanelId })
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && focusedPanelId) {
        e.preventDefault()
        toggleFullscreen.mutate({ id: focusedPanelId })
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [fullscreenPanel, focusedPanelId])

  const handleFullscreen = useCallback((panelId: string, isFullscreen: boolean) => {
    refetch()
  }, [refetch])

  const handleFocus = useCallback((panelId: string) => {
    setFocusedPanelId(panelId)
  }, [])

  const [originalLayout, setOriginalLayout] = useState<Layout[] | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [draggedItemId, setDraggedItemId] = useState<string | null>(null)
  const [temporaryLayout, setTemporaryLayout] = useState<Layout[] | null>(null)
  const [localLayout, setLocalLayout] = useState<Layout[] | null>(null)
  const [displacedPanels, setDisplacedPanels] = useState<Map<string, Layout>>(new Map())
  const gridRef = useRef<any>(null)
  const [isResizing, setIsResizing] = useState(false)
  const [resizingItemId, setResizingItemId] = useState<string | null>(null)
  const [resizeOriginalLayout, setResizeOriginalLayout] = useState<Layout[] | null>(null)
  const [pendingLayoutUpdates, setPendingLayoutUpdates] = useState<Layout[] | null>(null)

  const updatePositionsImmediately = useCallback((updates: any[]) => {
    if (updates.length > 0) {
      updatePositions.mutate({
        workspaceId,
        updates
      })
    }
  }, [workspaceId, updatePositions])

  const handleLayoutChange = useCallback((newLayout: Layout[]) => {
    console.log('📊 LAYOUT CHANGE', { isDragging, newLayout })
    if (allPanels.length === 0 || !newLayout) return
    
    // Always update local layout for immediate UI feedback
    setLocalLayout(newLayout)
    
    // During drag, don't persist changes to database
    if (isDragging) {
      console.log('⏸️ Skipping persist during drag')
      return
    }
    
    // Create a working copy of the layout to modify
    let modifiedLayout = [...newLayout]
    
    // If we're dragging, check for collisions with locked panels
    if (false && draggedItemId) {
      const draggedItemIndex = modifiedLayout.findIndex(item => item.i === draggedItemId)
      if (draggedItemIndex !== -1) {
        const draggedItem = modifiedLayout[draggedItemIndex]
        // Check if the dragged panel overlaps with any locked panels
        for (const panel of allPanels) {
          if (panel.id !== draggedItemId && panel.is_locked) {
            const lockedItem = modifiedLayout.find(l => l.i === panel.id)
            if (lockedItem) {
              const overlaps = !(
                draggedItem.x + draggedItem.w <= lockedItem.x ||
                draggedItem.x >= lockedItem.x + lockedItem.w ||
                draggedItem.y + draggedItem.h <= lockedItem.y ||
                draggedItem.y >= lockedItem.y + lockedItem.h
              )
              
              if (overlaps && originalLayout) {
                // Find the original position
                const originalItem = originalLayout.find(item => item.i === draggedItemId)
                if (originalItem) {
                  // Create a new modified layout with reverted position
                  modifiedLayout[draggedItemIndex] = {
                    ...draggedItem,
                    x: originalItem.x,
                    y: originalItem.y,
                    w: originalItem.w,
                    h: originalItem.h
                  }
                }
                break
              }
            }
          }
        }
      }
    }
    
    // dev: layout change details

    const optimizedLayout = modifiedLayout.map(item => ({
      ...item,
      w: Math.max(2, Math.min(24, item.w)), // Ensure width between 2-24 grid units
      h: Math.max(2, Math.min(50, item.h))  // Ensure height between 2-50 grid units
    }))

    const updates = optimizedLayout
      .filter(item => {
        const panel = allPanels.find(p => p.id === item.i)
        
        // NEVER update locked or minimized panels - minimized panels use temporary layout (h=2)
        if (panel && (panel.is_locked || panel.is_minimized)) {
          return false
        }
        
        const hasChanges = panel && (
          panel.position_x !== item.x ||
          panel.position_y !== item.y ||
          panel.width !== item.w ||
          panel.height !== item.h
        )
        
        return hasChanges
      })
      .map(item => ({
        id: item.i,
        position_x: item.x,
        position_y: item.y,
        width: Math.max(2, Math.min(24, item.w)), // Use clamped values
        height: Math.max(2, Math.min(50, item.h))
      }))

    if (updates.length > 0) {
      // persist updates
      updatePositionsImmediately(updates)
      
      // Trigger auto-save after real-time updates (debounced internally)
      if (!isDragging && !isResizing) {
        onLayoutPersistenceChange(optimizedLayout)
      }
    }
  }, [allPanels, updatePositionsImmediately, onLayoutPersistenceChange, isDragging, isResizing, draggedItemId, originalLayout])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDragging && originalLayout) {
        // snap back to original layout
      }
      
      if (e.key === 'Escape' && isResizing && resizeOriginalLayout) {
        // snap back to original layout
        
        const revertUpdates = resizeOriginalLayout
          .filter(item => {
            const panel = allPanels.find(p => p.id === item.i)
            return panel && !panel.is_locked
          })
          .map(item => ({
            id: item.i,
            position_x: item.x,
            position_y: item.y,
            width: item.w,
            height: item.h
          }))
        
        updatePositionsImmediately(revertUpdates)
        
        // Clean up resize state
        setIsResizing(false)
        setResizingItemId(null)
        setResizeOriginalLayout(null)
      }
    }

    if (isDragging || isResizing) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isDragging, originalLayout, isResizing, resizeOriginalLayout, allPanels, updatePositionsImmediately])

  const handlePanelDragStart = useCallback((layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    console.log('🔵 DRAG START', { oldItem, newItem })
    setOriginalLayout([...layout])
    setIsDragging(true)
    setDraggedItemId(oldItem.i)
    
    element.style.zIndex = '9998'
    
    // drag start
  }, [])

  const handlePanelDrag = useCallback((layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    // Let React Grid Layout handle the drag with vertical compaction
  }, [])

  const handlePanelDragStop = useCallback((layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    console.log('🔴 DRAG STOP', { oldItem, newItem, layout })
    
    // IMPORTANT: Keep the local layout updated with the final positions
    setLocalLayout(layout)
    
    // Clear drag state
    setIsDragging(false)
    setDraggedItemId(null)
    setTemporaryLayout(null)
    setDisplacedPanels(new Map())
    
    element.style.zIndex = ''
    
    // Check if we need to revert due to collision with locked panel
    let shouldRevert = false
    const draggedPanel = allPanels.find(p => p.id === newItem.i)
    
    if (draggedPanel) {
      // Check final position for overlap with locked panels
      for (const panel of allPanels) {
        if (panel.id !== draggedPanel.id && panel.is_locked) {
          const lockedItem = layout.find(l => l.i === panel.id)
          if (lockedItem) {
            const overlaps = !(
              newItem.x + newItem.w <= lockedItem.x ||
              newItem.x >= lockedItem.x + lockedItem.w ||
              newItem.y + newItem.h <= lockedItem.y ||
              newItem.y >= lockedItem.y + lockedItem.h
            )
            
            if (overlaps) {
              // overlap with locked panel
              shouldRevert = true
              break
            }
          }
        }
      }
    }
    
    if (shouldRevert && originalLayout) {
      // Revert to original layout
      // revert to original layout due to collision
      const revertUpdates = originalLayout
        .filter(item => {
          const panel = allPanels.find(p => p.id === item.i)
          return panel && !panel.is_locked && !panel.is_minimized
        })
        .map(item => ({
          id: item.i,
          position_x: item.x,
          position_y: item.y,
          width: item.w,
          height: item.h
        }))
      
      updatePositionsImmediately(revertUpdates)
    } else {
      // Normal save - skip locked panels
      const updates = layout
        .filter(item => {
          const panel = allPanels.find(p => p.id === item.i)
          return panel && !panel.is_locked && !panel.is_minimized
        })
        .map(item => ({
          id: item.i,
          position_x: item.x,
          position_y: item.y,
          width: item.w,
          height: item.h
        }))
      
      // save final positions
      console.log('💾 SAVING POSITIONS', updates)
      updatePositionsImmediately(updates)
    }
    
    setTimeout(() => {
      element.style.transition = ''
      setOriginalLayout(null)
    }, 300)
  }, [updatePositionsImmediately, allPanels, originalLayout, setLocalLayout])

  const handlePanelResizeStart = useCallback((layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    setResizeOriginalLayout([...layout])
    setIsResizing(true)
    setResizingItemId(oldItem.i)
    
    element.style.zIndex = '9997'
    
    // Track resize start for analytics
    const resizeData = {
      resizingPanel: oldItem.i,
      originalLayout: layout.length,
      timestamp: Date.now()
    }
  }, [])

  const handlePanelResize = useCallback((layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    // resizing handler
    
    // Don't modify layout during resize - just provide visual feedback
    // The actual push-down logic happens in onResizeStop
  }, [isResizing, resizingItemId, resizeOriginalLayout])

  const handlePanelResizeStop = useCallback((layout: Layout[], oldItem: Layout, newItem: Layout, placeholder: Layout, e: MouseEvent, element: HTMLElement) => {
    element.style.zIndex = ''
    
    // Check if this was a cancelled resize (mouse released without significant change)
    const isMinimalChange = resizeOriginalLayout && resizingItemId === newItem.i && (() => {
      const originalItem = resizeOriginalLayout.find(item => item.i === newItem.i)
      if (!originalItem) return false
      
      // Consider it cancelled if the change is very small (less than 0.5 grid units)
      const heightDiff = Math.abs(newItem.h - originalItem.h)
      const widthDiff = Math.abs(newItem.w - originalItem.w)
      return heightDiff < 0.5 && widthDiff < 0.5
    })()
    
    if (isMinimalChange && resizeOriginalLayout) {
      // Snap back to original layout
      // minimal resize, snap back
      
        const revertUpdates = resizeOriginalLayout
          .filter(item => {
            const panel = allPanels.find(p => p.id === item.i)
            return panel && !panel.is_locked && !panel.is_minimized
          })
        .map(item => ({
          id: item.i,
          position_x: item.x,
          position_y: item.y,
          width: item.w,
          height: item.h
        }))
      
      updatePositionsImmediately(revertUpdates)
    } else {
      // IMPLEMENT PUSH-DOWN BEHAVIOR AFTER RESIZE IS COMPLETE
      let finalLayout = [...layout]
      
      if (resizeOriginalLayout && resizingItemId === newItem.i) {
        const originalItem = resizeOriginalLayout.find(item => item.i === newItem.i)
        
        if (originalItem && newItem.h > originalItem.h) {
          // calculate push-down effect
          
          // Calculate how much the panel expanded downward
          const originalBottomY = originalItem.y + originalItem.h
          const newBottomY = newItem.y + newItem.h
          const expansionDistance = newBottomY - originalBottomY
          
          if (expansionDistance > 0) {
            // Sort panels by Y position to process from top to bottom
            const sortedPanels = [...finalLayout].sort((a, b) => a.y - b.y)
            const movedPanels = new Map<string, number>() // Track how far each panel was moved
            
            // Process each panel to see if it needs to be pushed
            sortedPanels.forEach(item => {
              if (item.i === newItem.i) return // Skip the resized panel
              
              const panel = allPanels.find(p => p.id === item.i)
              if (panel?.is_locked) return // Skip locked panels
              
              // Get the current panel position (might have been moved already)
              const currentY = movedPanels.get(item.i) ?? item.y
              const panelTopY = currentY
              const panelBottomY = currentY + item.h
              
              // Check if this panel overlaps with the resized panel's new area
              const overlapsHorizontally = !(
                item.x + item.w <= newItem.x || 
                item.x >= newItem.x + newItem.w
              )
              
              if (overlapsHorizontally) {
                // Check if panel is in the way of the expansion
                if (panelTopY < newBottomY && panelBottomY > originalBottomY) {
                  // Calculate minimum push distance with 1 grid unit gap
                  const minPushDistance = newBottomY - panelTopY + 1
                  const newY = panelTopY + minPushDistance
                  
                  // Update the panel's position in finalLayout
                  const panelIndex = finalLayout.findIndex(p => p.i === item.i)
                  if (panelIndex !== -1) {
                    finalLayout[panelIndex] = { ...finalLayout[panelIndex], y: newY }
                    movedPanels.set(item.i, newY)
                    
                    // pushing panel down
                    
                    // Check if this push affects other panels below (cascade effect)
                    const cascadeBottomY = newY + item.h
                    sortedPanels.forEach(otherItem => {
                      if (otherItem.i === item.i || otherItem.i === newItem.i) return
                      
                      const otherPanel = allPanels.find(p => p.id === otherItem.i)
                      if (otherPanel?.is_locked) return
                      
                      const otherCurrentY = movedPanels.get(otherItem.i) ?? otherItem.y
                      const otherOverlapsH = !(
                        otherItem.x + otherItem.w <= item.x || 
                        otherItem.x >= item.x + item.w
                      )
                      
                      if (otherOverlapsH && otherCurrentY < cascadeBottomY && otherCurrentY + otherItem.h > newY) {
                        const cascadePush = cascadeBottomY - otherCurrentY + 1
                        const otherNewY = otherCurrentY + cascadePush
                        const otherIndex = finalLayout.findIndex(p => p.i === otherItem.i)
                        if (otherIndex !== -1) {
                          finalLayout[otherIndex] = { ...finalLayout[otherIndex], y: otherNewY }
                          movedPanels.set(otherItem.i, otherNewY)
                          // cascade push
                        }
                      }
                    })
                  }
                }
              }
            })
          }
        }
      }
      
      // Update all positions in database
      const updates = finalLayout
        .filter(item => {
          const panel = allPanels.find(p => p.id === item.i)
          return panel && !panel.is_locked && !panel.is_minimized
        })
        .map(item => ({
          id: item.i,
          position_x: item.x,
          position_y: item.y,
          width: item.w,
          height: item.h
        }))
      
      if (updates.length > 0) {
        // save resize updates
        updatePositionsImmediately(updates)
        
        // Trigger auto-save after resize is complete
        setPendingLayoutUpdates(finalLayout)
        
        // Force re-fetch to update the UI immediately
        setTimeout(() => {
          refetch()
          // Trigger auto-save after UI has updated
          if (pendingLayoutUpdates) {
            onLayoutPersistenceChange({ lg: pendingLayoutUpdates })
            setPendingLayoutUpdates(null)
          }
        }, 100)
      }
    }
    
    // Clean up resize state
    setIsResizing(false)
    setResizingItemId(null)
    
    setTimeout(() => {
      element.style.transition = ''
      setResizeOriginalLayout(null)
    }, 200)
  }, [resizeOriginalLayout, resizingItemId, allPanels, updatePositionsImmediately])

  // Reset local layout when panels data changes from server
  // BUT only if we're not currently interacting with panels
  useEffect(() => {
    if (!isDragging && !isResizing) {
      // Only reset if this is a real data change, not just a re-render
      // The localLayout will stay in place during/after dragging
      console.log('🔄 Panel data updated, checking if reset needed')
      // We'll keep localLayout if it exists to maintain UI state
    }
  }, [allPanels])

  const layout = useMemo(() => {
    // IMPORTANT: Keep ALL panels in the layout to preserve positions
    // We'll hide panels visually, not remove them from the grid
    const gridLayout = allPanels.map(panel => {
      const isHidden = hiddenPanelIds.has(panel.id) || (focusMode && focusedPanelId && panel.id !== focusedPanelId)
      
      return {
        i: panel.id,
        x: panel.position_x,
        y: panel.position_y,
        w: panel.width,
        h: panel.is_minimized ? 1 : panel.height, // Minimized panels occupy exactly 1 grid row
        minW: 2,
        minH: panel.is_minimized ? 1 : 2,
        maxW: 24,
        maxH: panel.is_minimized ? 1 : 50, // Prevent resizing minimized panels vertically
        isDraggable: !panel.is_locked && !isHidden,
        isResizable: !panel.is_locked && !isHidden && !panel.is_minimized, // Disable resize for minimized panels
        static: panel.is_locked || panel.is_minimized // Locked panels are static - nothing can move them
      } as Layout
    })
    
    // grid layout conversion debug
    
    gridLayout.forEach(item => {
      if ((item.w || 0) < (item.minW || 2) || (item.h || 0) < (item.minH || 2)) {
        // size below minimum
      }
    })
    
    return gridLayout
  }, [allPanels, hiddenPanelIds, focusMode, focusedPanelId])



  const cols = 24

  if (allPanels.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-full p-8 bg-transparent', className)}>
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No panels yet</h3>
          <p className="text-gray-600">Add panels using the toolbar above to get started.</p>
        </div>
      </div>
    )
  }

  // Mobile single-panel mode: show only the focused panel (or first available)
  if (isMobile) {
    const focused = allPanels.find(panel => {
      try {
        const data = typeof panel.data === 'string' ? JSON.parse(panel.data) : panel.data
        return data?.isFocused
      } catch {
        return false
      }
    })
    // Prefer explicit focusedPanelId from UI store; fall back to server data; then to first available
    const byStore = focusedPanelId ? allPanels.find(p => p.id === focusedPanelId) : undefined
    const panelToShow = byStore || focused || allPanels.find(p => !p.is_minimized) || allPanels[0]
    // Swipe handlers to switch panels quickly
    const handleTouchStart = (e: React.TouchEvent) => {
      const t = e.changedTouches[0]
      touchStartXRef.current = t.clientX
      touchStartYRef.current = t.clientY
    }
    const handleTouchEnd = (e: React.TouchEvent) => {
      const t = e.changedTouches[0]
      const startX = touchStartXRef.current
      const startY = touchStartYRef.current
      touchStartXRef.current = null
      touchStartYRef.current = null
      if (startX == null || startY == null) return
      const dx = t.clientX - startX
      const dy = t.clientY - startY
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return
      const order = allPanels.map(p => p.id)
      const currentId = byStore?.id || focused?.id || order[0]
      const idx = order.findIndex(id => id === currentId)
      if (idx === -1) return
      const nextIdx = dx < 0 ? Math.min(order.length - 1, idx + 1) : Math.max(0, idx - 1)
      const nextId = order[nextIdx]
      if (nextId && nextId !== currentId) {
        setFocusedPanelId(nextId)
        setFocusMutation.mutate({ id: nextId, focused: true })
      }
    }

    return (
      <div className={cn('panel-workspace panel-workspace-container h-full flex-1 bg-transparent', className)}>
        <div className="h-full bg-transparent" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <PanelRenderer
            panel={{ ...panelToShow, data: panelToShow.data || {} }}
            workspaceId={workspaceId}
            onRefresh={refetch}
            onFullscreen={() => {}}
            onFocus={() => {}}
          />
        </div>
        {!isKeyboardOpen && <MobilePanelDock />}
      </div>
    )
  }

  return (
    <div className={cn('panel-workspace panel-workspace-container h-full flex-1 bg-transparent', className)} style={{ overflowX: 'hidden', overflowY: 'auto', scrollBehavior: 'auto' }}>
      <style>{`
        .react-grid-layout {
          position: relative;
          min-height: 100%;
          overflow: inherit; /* Inherit from parent to respect clipping */
        }
        
        .react-grid-item {
          transition: all 200ms cubic-bezier(0.23, 1, 0.32, 1);
          transition-property: left, top, width, height, transform;
          background: transparent; /* Allow frosted glass child to show */
          border: none;            /* Panel border handled by child card */
          border-radius: 0;        /* Prevent wrapper radius peeking through */
          overflow: visible;       /* Allow shadows to show */
          cursor: grab;
          /* All stacking context creators removed */
          /* isolation: auto; */
          /* transform-style: initial; */
          /* backface-visibility: initial; */
          contain: none;
        }
        
        .react-grid-item:hover {
          transform: none;
          border-color: transparent;
          cursor: grab;
        }
        
        .react-grid-item:active {
          cursor: grabbing;
        }
        
        .react-grid-item.react-grid-placeholder {
          background: rgba(34, 197, 94, 0.15);
          border: 2px solid rgba(34, 197, 94, 0.5);
          border-radius: 12px;
          box-shadow: 0 0 20px rgba(34, 197, 94, 0.4), inset 0 0 20px rgba(34, 197, 94, 0.1);
          transition: all 150ms cubic-bezier(0.23, 1, 0.32, 1);
          z-index: 2;
          user-select: none;
          opacity: 1;
          visibility: visible;
          display: block;
        }
        
        .react-grid-item.react-draggable-dragging {
          transition: none;
          z-index: 9998;
          cursor: grabbing;
          box-shadow: 0 10px 30px rgba(34, 197, 94, 0.3), 0 5px 15px rgba(34, 197, 94, 0.2);
        }
        
        .react-grid-item.resizing {
          z-index: 9997;
          box-shadow: 0 10px 30px rgba(34, 197, 94, 0.3), 0 5px 15px rgba(34, 197, 94, 0.2);
        }

        .react-grid-item.snapping {
          border: 2px solid rgba(20, 49, 151, 0.6);
          box-shadow: 0 0 0 4px rgba(20, 49, 151, 0.1);
        }

        .react-grid-item.focusing {
          animation: focusPulse 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        @keyframes focusPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.02); }
          100% { transform: scale(1); }
        }

        .react-grid-layout {
          transition: height 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .react-resizable-handle {
          position: absolute;
          background-color: transparent;
          border: none;
          opacity: 0;
          transition: opacity 0.2s ease;
        }

        .react-grid-item:hover .react-resizable-handle {
          opacity: 0;
        }

        .react-resizable-handle::before,
        .react-resizable-handle::after {
          display: none;
          content: none;
          background: none;
        }

        .react-resizable-handle > * {
          display: none;
        }
        
        .react-resizable-handle {
          background-image: none;
        }
        
        .react-resizable-handle:after {
          content: '';
          display: none;
          border: none;
        }

        .react-resizable-handle-se {
          bottom: 0;
          right: 0;
          width: 20px;
          height: 20px;
          cursor: se-resize;
          background: transparent;
          border: none;
          transform: none;
          z-index: 10;
        }

        .react-resizable-handle-ne {
          top: 0;
          right: 0;
          width: 20px;
          height: 20px;
          cursor: ne-resize;
          background: transparent;
          border: none;
          transform: none;
          z-index: 10;
        }

        .react-resizable-handle-sw {
          bottom: 0;
          left: 0;
          width: 20px;
          height: 20px;
          cursor: sw-resize;
          background: transparent;
          border: none;
          transform: none;
          z-index: 10;
        }

        .react-resizable-handle-nw {
          top: 0;
          left: 0;
          width: 20px;
          height: 20px;
          cursor: nw-resize;
          background: transparent;
          border: none;
          transform: none;
          z-index: 10;
        }

        .react-grid-item > .react-resizable-handle.react-resizable-handle-e {
          top: 0;
          right: -6px;
          bottom: 0;
          width: 12px;
          height: 100%;
          cursor: e-resize;
          transform: none;
          margin-top: 0;
        }

        .react-grid-item > .react-resizable-handle.react-resizable-handle-w {
          top: 0;
          left: -6px;
          bottom: 0;
          width: 12px;
          height: 100%;
          cursor: w-resize;
          transform: none;
          margin-top: 0;
        }

        .react-grid-item > .react-resizable-handle.react-resizable-handle-n {
          top: -6px;
          left: 0;
          right: 0;
          width: 100%;
          height: 12px;
          cursor: n-resize;
          transform: none;
          margin-left: 0;
        }

        .react-grid-item > .react-resizable-handle.react-resizable-handle-s {
          bottom: -6px;
          left: 0;
          right: 0;
          width: 100%;
          height: 12px;
          cursor: s-resize;
          transform: none;
          margin-left: 0;
        }
      `}</style>
      
      <FixedGridLayout
        ref={gridRef}
        className="layout"
        layout={localLayout || layout}
        cols={cols}
        onLayoutChange={handleLayoutChange}
        isDraggable={!isMobile}
        isResizable={!isMobile}
        resizeHandles={['se', 'e', 's', 'w', 'n', 'nw', 'ne', 'sw']}
        margin={[12, 12]}
        containerPadding={[16, 16]}
        rowHeight={25}
        draggableHandle=".panel-drag-handle"
        useCSSTransforms={true}
        preventCollision={false}
        compactType="vertical"
        autoSize={true}
        draggableCancel="input,textarea,button,select,option,.panel-content"
        transformScale={1}
        width={containerWidth}
        onDrag={handlePanelDrag}
        onDragStart={handlePanelDragStart}
        onDragStop={handlePanelDragStop}
        onResize={handlePanelResize}
        onResizeStart={handlePanelResizeStart}
        onResizeStop={handlePanelResizeStop}
      >
        {allPanels.map(panel => {
          // Hide panel if it's in hiddenPanelIds OR if we're in focus mode and it's not the focused panel
          const shouldHide = hiddenPanelIds.has(panel.id) || (focusMode && focusedPanelId && panel.id !== focusedPanelId)
          
          return (
            <div 
              key={panel.id} 
              className="panel-container"
              data-panel-type={panel.type}
              style={{ 
                visibility: shouldHide ? 'hidden' : 'visible',
                pointerEvents: shouldHide ? 'none' : 'auto'
              }}
            >
              <PanelRenderer 
                panel={{ ...panel, data: panel.data || {} }}
                workspaceId={workspaceId}
                onRefresh={refetch}
                onFullscreen={handleFullscreen}
                onFocus={handleFocus}
              />
            </div>
          )
        })}
      </FixedGridLayout>

      {/* No separate fullscreen overlay needed - PanelRenderer handles fullscreen with CSS classes */}
    </div>
  )
}

/**
 * PanelWorkspace - Modern drag-and-drop panel workspace
 * Replaces legacy Packery.js with React Grid Layout
 */